import React from 'react'

import { DrawerPageAsync } from '@Components/Drawer/Page'
import { PageType, RteField } from '@Generated/graphql'
import { resolveRTEField } from '@Globals/CMS/RTEReferences'

import styles from './RTEContent.module.scss'
import { jsonToReact, jsonToText, RTENode, SerializeOptions } from './serialize'

// default rendering overrides for RTE content elements
const renderRTEElement: NonNullable<SerializeOptions['renderElement']> = (node, { children, getAttributes }) => {
  const modalID = node.type === 'reference' ? node.attrs?.['data-modal-id'] : undefined

  if (typeof modalID === 'string' && modalID !== 'contact-us') {
    // Reuse the attributes the node would normally render (href, classes, colours, …) on the trigger
    // so styling stays 1:1, minus `data-modal-id` so the legacy global modal click-listener
    // (Modal/Provider) ignores it and only the drawer opens.
    const elementProps = getAttributes()
    delete elementProps['data-modal-id']

    return (
      <DrawerPageAsync
        pageID={modalID}
        pageType={PageType.Modal}
        triggerNativeButton={false}
        triggerRender={triggerProps => (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid -- intentional <a href="#"> drawer trigger; keeps RTE link styling 1:1
          <a
            {...elementProps}
            {...triggerProps}
            className=""
            onClick={(event) => {
              event.preventDefault()
              elementProps.onClick?.(event)
              triggerProps.onClick?.(event)
            }}
          >
            {children}
          </a>
        )}
      />
    )
  }

  return undefined
}

/**
 * Resolves an RTE field's references (merged with `dynamicVariables`) and serializes it to elements,
 * applying the global `renderRTEElement` overrides.
 */
export const convertRTEToReact = (
  data: RteField | null | undefined,
  dynamicVariables: Record<string, string> = {},
  options: SerializeOptions = {},
): React.ReactNode => {
  const resolved = resolveRTEField(data, dynamicVariables)
  if (!resolved) {
    return null
  }
  // A caller-supplied `renderElement` wins; otherwise the global overrides apply.
  const renderElement: SerializeOptions['renderElement'] = (node, ctx) => options.renderElement?.(node, ctx) ?? renderRTEElement(node, ctx)
  return jsonToReact(resolved as RTENode, { ...options, renderElement })
}

/**
 * Resolves an RTE field's references and returns its plain-text content — for places that need a
 * string rather than rendered elements, e.g. structured data / JSON-LD descriptions.
 */
export const convertRTEToText = (
  data: RteField | null | undefined,
  dynamicVariables: Record<string, string> = {},
): string => {
  const resolved = resolveRTEField(data, dynamicVariables)
  return resolved ? jsonToText(resolved as RTENode) : ''
}

type RTEContentProps<T extends React.ElementType> = {
  data: RteField | null | undefined
  dynamicVariables?: Record<string, string>
  elementType?: T
  options?: SerializeOptions
  /** Wrapper element to render. Defaults to `div`; its props are typed to match. */
} & Omit<React.ComponentPropsWithoutRef<T>, 'data' | 'dynamicVariables' | 'elementType' | 'options'>

const RTEContentInner = <T extends React.ElementType = 'div'>({
  data,
  dynamicVariables,
  elementType,
  options,
  ...props
}: RTEContentProps<T>) => {
  const Tag = (elementType || 'div') as React.ElementType
  const content = convertRTEToReact(data, dynamicVariables, options)

  if (!content) {
    return null
  }

  // TODO: check if we really want to override className if a given className comes from the outside?
  // TODO: product list block description with links will be blue if we don't override?
  return (
    <Tag className={styles.rteContent} {...props}>
      {content}
    </Tag>
  )
}

/**
 * Renders a Contentstack JSON RTE field, resolving references/dynamic variables first and applying
 * the global element overrides (e.g. modal references rendered as a drawer). The wrapper defaults to
 * a `<div>`; pass `elementType` (e.g. `elementType="blockquote"`) to change it — the wrapper props match it.
 *
 * Memoised: only re-renders when props change by reference. Pass a stable `renderElement`/`marks`/
 * `dynamicVariables` (module scope or `useMemo`/`useCallback`) to keep it from re-rendering.
 */
export const RTEContent = React.memo(RTEContentInner) as typeof RTEContentInner
